/* A filter as a checklist in a drop-down: "All", then one box per option.
 *
 * The same control for every filter of the top bar, so projects and value
 * streams are picked the same way. An empty selection means "all": a filter
 * nobody touched filters nothing.
 */

import { useEffect, useRef, useState } from 'react'
import { Icon } from './icons'

interface Props {
  label: string
  options: { id: string; label: string }[]
  /* Ticked ids; empty = all. */
  value: string[]
  onChange: (next: string[]) => void
}

export function MultiSelect({ label, options, value, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  const all = value.length === 0
  const on = (id: string) => !all && value.includes(id)
  const toggle = (id: string) => {
    // From "All", a click picks that one option; then each click adds or
    // removes one. Everything ticked, or nothing, is "all" - never an empty chart.
    const next = all ? [id] : value.includes(id) ? value.filter((x) => x !== id) : [...value, id]
    onChange(next.length === options.length ? [] : next)
  }
  const summary = all
    ? 'All'
    : value.length === 1
      ? options.find((o) => o.id === value[0])?.label ?? value[0]
      : `${value.length} selected`

  return (
    <div className="multi" ref={box}>
      <button
        type="button"
        className={`multi-button ${all ? '' : 'active'}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="multi-label">{label}</span>
        <span className="multi-value">{summary}</span>
        <Icon name="chevron" size={14} />
      </button>
      {open && (
        <div className="multi-menu" role="listbox" aria-multiselectable="true">
          <label className={`multi-option all ${all ? 'on' : ''}`}>
            <input type="checkbox" checked={all} onChange={() => onChange([])} />
            All
          </label>
          {options.map((o) => (
            <label key={o.id} className={`multi-option ${on(o.id) ? 'on' : ''}`}>
              <input type="checkbox" checked={on(o.id)} onChange={() => toggle(o.id)} />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
